import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { useToast } from "../../../../hooks/toast";

import api from "../../../../service";
import generateHeaders from "../../../../utils/generateHeaders";
import { formatCurrency } from "../../utils";
import { getDefaultersData } from "../../../../redux/modules/defaulters/actions";

import {
  customStyles,
  EmptyContainer,
  HighlightText,
  ModalButtonsWrapper,
  ModalSubtitle,
  ModalTitle,
  TableContainer,
} from "../../styles";
import { Modal } from "antd";
import Button from "../button";
import DataTable from "react-data-table-component";
import {
  AiOutlineExclamationCircle,
  AiOutlineLoading3Quarters,
} from "react-icons/ai";
import BackIcon from "../Icons/backIcon";
import { BiChevronUp } from "react-icons/bi";
import ForwardIcon from "../Icons/forwardIcon";
import { CheckCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import "moment/locale/pt";

export default function DefaultersTable({
  data = [],
  loading,
  omitArray = [],
}) {
  const intl = useIntl();
  const dispatch = useDispatch();

  const removeDefaulter = useCallback(
    (id) => {
      dispatch(getDefaultersData());
    },
    [dispatch]
  );

  const columns = useMemo(
    () => [
      {
        id: "sales_person",
        name: <FormattedMessage id={"defaulters.table.sales_person"} />,
        selector: (row) => (
          <span title={row.salesPerson}>{row.salesPerson}</span>
        ),
        sortable: true,
        sortFunction: (rowA, rowB) => {
          const a = (rowA.salesPerson || "").toLowerCase();
          const b = (rowB.salesPerson || "").toLowerCase();
          return a.localeCompare(b);
        },
        maxWidth: "140px",
        omit: !omitArray.some(
          (item) => item === "defaulters.table.sales_person"
        ),
      },
      {
        id: "analysis_date",
        name: <FormattedMessage id={"defaulters.table.analysis_date"} />,
        selector: (row) => {
          moment.locale(intl.locale === "en" ? "en" : "pt");
          const date = moment(row.analysisDate);
          return (
            <span
              style={{
                textTransform: "capitalize",
              }}
            >
              {date.isValid() ? (
                date.format("MMM YYYY")
              ) : (
                <FormattedMessage id="invalid_date" />
              )}
            </span>
          );
        },
        sortable: true,
        maxWidth: "140px",
        omit: !omitArray.some(
          (item) => item === "defaulters.table.analysis_date"
        ),
      },
      {
        id: "transaction_currency",
        name: <FormattedMessage id={"defaulters.table.transaction_currency"} />,
        selector: (row) => row.currency,
        sortable: true,
        maxWidth: "80px",
        omit: !omitArray.some(
          (item) => item === "defaulters.table.transaction_currency"
        ),
      },
      {
        id: "customer_name",
        name: <FormattedMessage id={"defaulters.table.customer_name"} />,
        selector: (row) => (
          <span title={row.customerName}>{row.customerName}</span>
        ),
        sortable: true,
        sortFunction: (rowA, rowB) => {
          const a = (rowA.customerName || "").toLowerCase();
          const b = (rowB.customerName || "").toLowerCase();
          return a.localeCompare(b);
        },
        omit: !omitArray.some(
          (item) => item === "defaulters.table.customer_name"
        ),
      },
      {
        id: "transaction_number",
        name: <FormattedMessage id={"defaulters.table.transaction_number"} />,
        selector: (row) => row.transactionNumber,
        sortable: true,
        maxWidth: "130px",
        omit: !omitArray.some(
          (item) => item === "defaulters.table.transaction_number"
        ),
      },

      {
        id: "total",
        name: <FormattedMessage id={"defaulters.table.total"} />,
        selector: (row) => formatCurrency(row.total),
        sortable: true,
        maxWidth: "140px",
        omit: !omitArray.some((item) => item === "defaulters.table.total"),
      },
      {
        id: "due",
        name: <FormattedMessage id={"defaulters.table.due"} />,
        selector: (row) => formatCurrency(row.due),
        sortable: true,
        maxWidth: "140px",
        omit: !omitArray.some((item) => item === "defaulters.table.due"),
      },
      {
        id: "overdue",
        name: <FormattedMessage id={"defaulters.table.overdue"} />,
        selector: (row) => formatCurrency(row.overdue),
        sortable: true,
        maxWidth: "140px",
        omit: !omitArray.some((item) => item === "defaulters.table.overdue"),
      },
      {
        id: "paid",
        name: <FormattedMessage id={"defaulters.table.paid"} />,
        selector: (row) => formatCurrency(row.paid),
        sortable: true,
        maxWidth: "140px",
        omit: !omitArray.some((item) => item === "defaulters.table.paid"),
      },
      {
        id: "opportunity",
        name: <FormattedMessage id={"defaulters.table.opportunity"} />,
        selector: (row) => row.opportunity,
        sortable: true,
        maxWidth: "130px",
        omit: !omitArray.some(
          (item) => item === "defaulters.table.opportunity"
        ),
      },

      {
        id: "actionRow",
        maxWidth: "140px",
        name: <FormattedMessage id={"actions"} />,
        selector: (row) => (
          <ActionRow
            id={row.id}
            removeDefaulter={removeDefaulter}
            client={row.customerName}
          />
        ),
        center: true,
        omit: !omitArray.some((item) => item === "actions"),
      },
    ],
    [omitArray, removeDefaulter]
  );

  return (
    <TableContainer>
      <DataTable
        data={data}
        noDataComponent={<Empty loading={loading} />}
        responsive
        columns={columns}
        fixedHeader
        fixedHeaderScrollHeight="calc(100vh - 300px)"
        customStyles={{
          ...customStyles,
          headRow: {
            style: {
              backgroundColor: "#f6f6f6",
              position: "sticky",
              top: 0,
              zIndex: 1,
            },
          },
        }}
        sortIcon={<BiChevronUp style={{ display: "flex" }} />}
        paginationComponentOptions={options({
          locale: intl.locale,
        })}
        persistTableHead
        pagination
        paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 30, 50, 100]}
      />
    </TableContainer>
  );
}

const options = ({ locale }) => {
  return {
    rowsPerPageText: <FormattedMessage id={"rows_per_page"} />,
    rangeSeparatorText: locale === "en" ? "of" : "de",
    noRowsPerPage: false,
    selectAllRowsItem: true,
    selectAllRowsItemText: locale === "en" ? "All" : "Tudo",
  };
};

function Empty({ highlightText = "empty.defaulters", loading }) {
  if (loading)
    return (
      <EmptyContainer loading>
        <AiOutlineLoading3Quarters />
      </EmptyContainer>
    );
  return (
    <EmptyContainer>
      <AiOutlineExclamationCircle size={30} />
      <HighlightText>
        <FormattedMessage id={highlightText} />
      </HighlightText>
    </EmptyContainer>
  );
}

function ActionRow({ id, removeDefaulter, client }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <Button
        variant={"green"}
        size={"sm"}
        Icon={<CheckCircleOutlined />}
        label={"defaulters.remove"}
        noShadow
        onClick={() => setOpen(true)}
      />
      <UpdateDefaulter
        open={open}
        setOpen={setOpen}
        id={id}
        removeDefaulter={removeDefaulter}
        client={client}
      />
    </div>
  );
}

function UpdateDefaulter({ open, setOpen, id, removeDefaulter, client }) {
  const intl = useIntl();
  const { addToast } = useToast();
  const { token } = useSelector((state) => state.auth);

  const handleDelete = async () => {
    try {
      const options = generateHeaders(token);
      await api.delete(`/accounts-receivable/debtors/${id}`, options);
      removeDefaulter(id);

      addToast({
        title: intl.formatMessage({ id: "success" }),
        description: intl.formatMessage({
          id: "defaulters.defaulter_removed",
        }),
        type: "success",
      });

      setOpen(false);
    } catch (error) {
      addToast({
        title: intl.formatMessage({ id: "success" }),
        description: intl.formatMessage({
          id: "defaulters.defaulter_remove_error",
        }),
        type: "error",
      });
    }
  };

  return (
    <Modal
      open={open}
      onCancel={() => setOpen(false)}
      centered={true}
      closable={false}
      width={600}
      footer={""}
      className="modalLesson"
      destroyOnClose
    >
      <ModalTitle>
        {intl.formatMessage(
          {
            id: "defaulters.remove_defaulter",
          },
          { client }
        )}
      </ModalTitle>

      <ModalSubtitle>
        {intl.formatMessage({
          id: "irreversible_action",
        })}
      </ModalSubtitle>

      <ModalButtonsWrapper>
        <Button
          label={"goback"}
          variant={"secondary"}
          Icon={<BackIcon size={16} />}
          onClick={() => setOpen(false)}
          shadow
        />
        <Button
          label={"confirm"}
          Icon={<ForwardIcon />}
          onClick={handleDelete}
          shadow
        />
      </ModalButtonsWrapper>
    </Modal>
  );
}
